import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

// Check circle SVG component
const CheckCircle = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="6" fill="#15803d" />
    <path d="M3.5 6l1.5 1.5L8.5 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Frame202: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();
  const [currentCard, setCurrentCard] = useState(0);
  const [nextEnabled, setNextEnabled] = useState(false);

  // Get data from session
  const properties = session.taskProgress.properties?.answers?.properties || [];
  const personalItems = session.taskProgress.personal?.personalItems || [];
  const executors = session.taskProgress.executors?.executors || [];
  const heirs = session.taskProgress.heirs?.heirs || [];
  const professionalContacts = session.taskProgress.professional?.contacts || [];

  // Calculate asset total
  let totalAssetValue = 0;
  properties.forEach((property: unknown) => {
    let netWorth = 0, mortgage = 0;
    if (typeof property.netWorth === 'number') netWorth = property.netWorth;
    else if (typeof property.netWorth === 'string') {
      const num = Number(property.netWorth.replace(/[^\d.-]/g, ''));
      if (!isNaN(num)) netWorth = num;
    }
    if (typeof property.mortgage === 'number') mortgage = property.mortgage;
    else if (typeof property.mortgage === 'string') {
      const num = Number(property.mortgage.replace(/[^\d.-]/g, ''));
      if (!isNaN(num)) mortgage = num;
    }
    const equity = netWorth - mortgage;
    if (!isNaN(equity)) totalAssetValue += equity;
  });

  const formattedTotal = totalAssetValue.toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD' 
  });

  // Define cards for the rolodex animation
  const cards = [
    {
      title: "Assets",
      items: [
        `${properties.length} properties documented`,
        `Total value: ${formattedTotal}`,
        "Property details captured"
      ]
    },
    {
      title: "Executors",
      items: [
        `${executors.length} executors selected`,
        "Contact information verified",
        "Responsibilities assigned"
      ]
    },
    {
      title: "Heirs",
      items: [
        `${heirs.length} heirs identified`,
        "Inheritance preferences set",
        "Legal documentation ready"
      ]
    },
    {
      title: "Personal Items",
      items: [
        `${personalItems.length} belongings catalogued`,
        "Sentimental items noted",
        "Distribution preferences recorded"
      ]
    },
    {
      title: "Professional Support",
      items: [
        `${professionalContacts.length} contacts added`,
        "Attorney information stored",
        "Financial advisor details saved"
      ]
    },
    {
      title: "Final Wishes",
      items: [
        "Funeral preferences documented",
        "Memorial instructions recorded",
        "End-of-life wishes captured"
      ]
    }
  ];

  useEffect(() => {
    // Enable Next button after 5 seconds
    const enableNextTimer = setTimeout(() => {
      setNextEnabled(true);
    }, 5000);

    // Auto-proceed after 20 seconds
    const autoProceedTimer = setTimeout(() => {
      navigate('/frame-203');
    }, 20000);

    // Card animation cycle (every 3 seconds)
    const cardInterval = setInterval(() => {
      setCurrentCard((prev) => (prev + 1) % cards.length);
    }, 3000);

    return () => {
      clearTimeout(enableNextTimer);
      clearTimeout(autoProceedTimer);
      clearInterval(cardInterval);
    };
  }, [navigate, cards.length]);

  const handleClose = () => {
    navigate('/frame-196');
  };

  const handleNext = () => {
    if (nextEnabled) {
      navigate('/frame-203');
    }
  };

  return (
    <div className="min-h-screen w-full bg-stone-50 flex flex-col">
      {/* Main Content Container */}
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 w-6 h-6 bg-stone-500 hover:bg-stone-600 transition-colors rounded"
        />
        
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-cyan-900 text-2xl sm:text-3xl font-semibold font-['Test_Domaine_Display'] leading-loose">
            Final review
          </h1>
        </div>
        
        {/* Main Content */}
        <div className="mb-12">
          <div className="max-w-4xl text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            This walkthrough will help you prepare all your estate documentation for creating a legally conforming will and/or trust.
            <br/><br/>
            Analyzing your work so far...
          </div>
        </div>
        
        {/* Animated Rolodex Cards - Responsive Center */}
        <div className="flex justify-center mb-16">
          <div className="relative">
            {/* Background cards for stacking effect */}
            <div className="w-80 h-24 absolute top-8 left-6 opacity-40 bg-white rounded-lg shadow-md transform rotate-1" />
            <div className="w-80 h-28 absolute top-4 left-3 opacity-80 bg-white rounded-lg shadow-md transform -rotate-1" />
            
            {/* Main animated card */}
            <div className="w-96 h-28 bg-white rounded-lg shadow-lg border border-stone-300 transition-all duration-500 ease-in-out transform relative z-10">
              <div className="p-4">
                <div className="text-black text-base font-semibold font-['Work_Sans'] mb-2">
                  {cards[currentCard].title}
                </div>
                {cards[currentCard].items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <div className="text-stone-800 text-xs font-normal font-['Work_Sans']">
                      {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Chat button - Fixed position */}
        <div className="fixed bottom-6 right-6 w-12 h-12 bg-white rounded-3xl shadow-lg flex items-center justify-center">
          <div className="w-5 h-5 bg-cyan-900" />
        </div>
      </div>

      {/* Footer Navigation - Responsive */}
      <footer className="w-full bg-stone-50 border-t border-stone-200">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-cyan-900 rounded-md shadow-lg mx-4 mb-4 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              
              {/* Footer Content */}
              <div className="flex-1">
                <h3 className="text-stone-50 text-xl font-medium font-['Work_Sans'] leading-relaxed mb-2">
                  Finalization
                </h3>
                <p className="text-stone-50/50 text-xs font-normal font-['Work_Sans'] leading-none">
                  Approximately 10 minutes
                </p>
                
                {/* Progress bar */}
                <div className="mt-4 w-full max-w-2xl">
                  <div className="w-full h-[5px] bg-stone-400 rounded-md relative">
                    <div className="w-32 h-[5px] bg-green-900 rounded-md absolute left-0 top-0" />
                  </div>
                </div>
              </div>
              
              {/* Next Button */}
              <button 
                onClick={handleNext}
                disabled={!nextEnabled}
                className={`px-6 py-3 rounded inline-flex justify-center items-center gap-2.5 transition-all duration-300 whitespace-nowrap ${
                  nextEnabled 
                    ? 'bg-cyan-700 hover:bg-cyan-800 cursor-pointer opacity-100' 
                    : 'bg-cyan-700 opacity-50 cursor-not-allowed'
                }`}
              >
                <span className="text-center text-stone-50 text-base font-medium font-['Work_Sans'] leading-none">
                  Next →
                </span>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Frame202; 